import {HttpClient, HttpHeaders} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

import { ExportConfig, ExportOptions, ProjectDto, RoleDto } from '@api/projects/interfaces';
import { IParticipantConfig, IProjectConfig, IUserGroup, IWorkflow } from '@interfaces/interfaces';
import { ProjectUserDto } from '@app/services/project/project.service';


@Injectable({
	providedIn: 'root'
})
export class ProjectRestService {

	constructor(private httpClient: HttpClient) { }

	listProjects$(): Observable<ProjectDto[]> {
		const url = `${environment.serverEndpoint}/api/projects`;
		return this.httpClient.get<ProjectDto[]>(url);
	}

	getProject$(id: string): Observable<ProjectDto> {
		const url = `${environment.serverEndpoint}/api/projects/${id}`;
		return this.httpClient.get<ProjectDto>(url);
	}

	createProject(projectDto: ProjectDto): Promise<ProjectDto> {
		const url = `${environment.serverEndpoint}/api/projects`;
		return this.httpClient.post<ProjectDto>(url, projectDto).toPromise();
	}

	getWorkflow$(id: string): Observable<IWorkflow> {
		const url = `${environment.serverEndpoint}/api/projects/${id}/config/workflow`;
		return this.httpClient.get<IWorkflow>(url);
	}

	async getWorkflow(id: string): Promise<IWorkflow> {
		return await this.getWorkflow$(id).toPromise();
	}

	getParticipantsConfig$(projectID: number|string): Observable<IParticipantConfig> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/config/participant`;
		return this.httpClient.get<IParticipantConfig>(url);
	}

	async getParticipantsConfig(projectID: number|string): Promise<IParticipantConfig> {
		return await this.getParticipantsConfig$(projectID).toPromise();
	}

	getExportsConfig$(projectID: number|string): Observable<ExportConfig[]> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/exports`;
		return this.httpClient.get<ExportConfig[]>(url);
	}

	async getExportsConfig(projectID: number|string): Promise<ExportConfig[]> {
		return await this.getExportsConfig$(projectID).toPromise();
	}

	postExports$(projectID: number|string, exportId: string, exportOptions: ExportOptions): Observable<string> {
		const headers = new HttpHeaders().set('Content-Type', 'application/json');

		const url = `${environment.serverEndpoint}/api/projects/${projectID}/exports/${exportId}`;
		return this.httpClient.post(url, exportOptions, {headers, responseType: 'text'});
	}

	async postExports(projectID: number|string, exportId: string, exportOptions: ExportOptions): Promise<string> {
		return await this.postExports$(projectID, exportId, exportOptions).toPromise();
	}

	updateProject$(projectDto: ProjectDto): Observable<ProjectDto> {
		const url = `${environment.serverEndpoint}/api/projects/${projectDto.project_id}`;
		return this.httpClient.patch<ProjectDto>(url, projectDto);
	}

	uploadProjectArchive(projectID: string, zipFile: File): Observable<ProjectDto> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/config`;
		return this.httpClient.put<ProjectDto>(url, zipFile);
	}

	getProjectRoles$(projectID: string): Observable<RoleDto[]> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/roles`;
		return this.httpClient.get<RoleDto[]>(url);
	}

	getGlobalRoles$(): Observable<RoleDto[]> {
		const url = `${environment.serverEndpoint}/api/roles`;
		return this.httpClient.get<RoleDto[]>(url);
	}

	getProjectGroups$(projectID: string): Observable<IUserGroup[]> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/groups`;
		return this.httpClient.get<IUserGroup[]>(url);
	}

	getProjectUsers$(projectID: string): Observable<ProjectUserDto[]> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users`;
		return this.httpClient.get<ProjectUserDto[]>(url);
	}

	getProjectUser$(projectID: string, userName: string): Observable<ProjectUserDto> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users/${userName}`;
		return this.httpClient.get<ProjectUserDto>(url);
	}

	getProjectUserPermissions$(projectID: string, userName: string): Observable<string[]> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users/${userName}/permissions`;
		return this.httpClient.get<string[]>(url);
	}

	updateProjectUser$(projectID: string, dto: ProjectUserDto): Observable<ProjectUserDto> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users/${dto.username}`;
		return this.httpClient.patch<ProjectUserDto>(url, dto);
	}

	assignProjectUser$(projectID: string, userName: string): Observable<void> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users/${userName}/assign`;
		return this.httpClient.post<void>(url, {});
	}

	deleteProjectUser$(projectID: string, userName: string): Observable<void> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users/${userName}`;
		return this.httpClient.delete<void>(url);
	}

	async createProjectUser(projectID: string, projectuser: ProjectUserDto): Promise<ProjectUserDto> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users/${projectuser.username}`;
		return this.httpClient.put<ProjectUserDto>(url, projectuser).toPromise();
	}

	projectUserPermissions$(projectID: string, projectUsername: string): Observable<string[]> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/users/${projectUsername}/permissions`;
		return this.httpClient.get<string[]>(url);
	}

	async projectUserPermissions(projectID: string, projectUsername: string): Promise<string[]> {
		return this.projectUserPermissions$(projectID, projectUsername).toPromise();
	}
	
	projectConfig$(projectID: string): Observable<IProjectConfig> {
		const url = `${environment.serverEndpoint}/api/projects/${projectID}/config/config`;
		return this.httpClient.get<IProjectConfig>(url);
	}
	
	async projectConfig(projectID: string): Promise<IProjectConfig> {
		return this.projectConfig$(projectID).toPromise();
	}
}
